import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ListItem,
  ListItemCreate,
  ListItemReorder,
  ListItemUpdate,
} from '@travel-planner/shared';
import { api } from '../api/client';

export const itemsKey = (tripId: string, countryId: string) =>
  ['items', tripId, countryId] as const;

/** All items for the whole trip (the map + itinerary palette read this). */
export const tripItemsKey = (tripId: string) => ['items', tripId, 'all'] as const;

// Any item change can affect the per-country board, the trip-wide map, and the
// itinerary palette, so invalidate the whole ['items', tripId] subtree.
const invalidateItems = (tripId: string) => ({ queryKey: ['items', tripId] as const });

export function useListItems(tripId: string | null, countryId: string | null) {
  return useQuery<ListItem[]>({
    queryKey: tripId && countryId ? itemsKey(tripId, countryId) : ['items', 'none'],
    queryFn: () => api.get<ListItem[]>(`/trips/${tripId}/items?countryId=${countryId}`),
    enabled: Boolean(tripId && countryId),
  });
}

export function useTripItems(tripId: string | null) {
  return useQuery<ListItem[]>({
    queryKey: tripId ? tripItemsKey(tripId) : ['items', 'none-all'],
    queryFn: () => api.get<ListItem[]>(`/trips/${tripId}/items`),
    enabled: Boolean(tripId),
  });
}

export function useCreateItem(tripId: string, _countryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ListItemCreate) => api.post<ListItem>(`/trips/${tripId}/items`, input),
    onSuccess: () => qc.invalidateQueries(invalidateItems(tripId)),
  });
}

export function useUpdateItem(tripId: string, _countryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, patch }: { itemId: string; patch: ListItemUpdate }) =>
      api.patch<ListItem>(`/trips/${tripId}/items/${itemId}`, patch),
    onSuccess: () => qc.invalidateQueries(invalidateItems(tripId)),
  });
}

export function useDeleteItem(tripId: string, _countryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => api.del<void>(`/trips/${tripId}/items/${itemId}`),
    onSuccess: () => qc.invalidateQueries(invalidateItems(tripId)),
  });
}

export function useReorderItems(tripId: string, _countryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ListItemReorder) =>
      api.put<{ ok: true }>(`/trips/${tripId}/items/reorder`, input),
    onSettled: () => qc.invalidateQueries(invalidateItems(tripId)),
  });
}
